import type {
  FilterChangedEvent,
  GridReadyEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { defaultGridOptions } from "src/config/agGrid";
import { publishGridRowCount } from "src/hooks/useGridRowCount";
import type { Nullable } from "src/ts-types";

import {
  clearPeopleRowSelection,
  setPeopleRowSelection,
} from "src/actions/user";
import { userStatus } from "src/constants/users";
import { getPeopleRowData, getRoleTitles } from "src/reducers";
import type { AppState, UID } from "src/types";
import { overlayComponentParams, selectionColumnDef } from "./constants";
import Header from "./Header";
import Info from "./Info";
import { ContactsTableContainer, GridWrapper } from "./styles";
import { getColumns } from "./Table/columns";

const People = () => {
  const dispatch = useDispatch();
  const roleTitles = useSelector(({ app }: { app: AppState }) =>
    getRoleTitles(app),
  );
  const rowData = useSelector(({ app }: { app: AppState }) =>
    getPeopleRowData(app),
  );

  const [selectedUser, setSelectedUser] = useState<Nullable<UID>>(null);
  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    return () => {
      dispatch(clearPeopleRowSelection());
    };
  }, [dispatch]);

  const handleSearch = (value: string) => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption("quickFilterText", value);
    }
  };

  const onGridReady = async (params: GridReadyEvent) => {
    await params.api.setColumnFilterModel("status", [
      userStatus.active,
      userStatus.pending,
    ]);
    params.api.onFilterChanged();
  };

  /**
   * Synchronizes the Ag-Grid selection with the Redux store.
   * This ensures that other components (like the Header) have access to the currently selected UIDs.
   */
  const onSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedUids = event.api
      .getSelectedNodes()
      .map((node) => node.data?.uid)
      .filter(Boolean);

    dispatch(setPeopleRowSelection(selectedUids));
  };

  const columns = useMemo(
    () =>
      getColumns({
        onUserClick: setSelectedUser,
        roleTitles: roleTitles,
      }),
    [roleTitles],
  );

  const onFilterChanged = (event: FilterChangedEvent<any, any>) => {
    publishGridRowCount("people-contacts", event.api.getDisplayedRowCount());
  };

  return (
    <>
      <Header handleSearch={handleSearch} />
      <ContactsTableContainer>
        <GridWrapper>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            onFilterChanged={onFilterChanged}
            columnDefs={columns}
            enableCellTextSelection={true}
            onGridReady={onGridReady}
            onSelectionChanged={onSelectionChanged}
            rowSelection={{
              mode: "multiRow",
              enableClickSelection: false,
              headerCheckbox: false,
            }}
            selectionColumnDef={selectionColumnDef}
            animateRows={true}
            cellSelection={false}
            getRowId={(params) => params.data?.uid || ""}
            overlayComponentParams={overlayComponentParams}
            {...defaultGridOptions}
          />
        </GridWrapper>
      </ContactsTableContainer>
      {selectedUser && (
        <Info uid={selectedUser} handleClose={() => setSelectedUser(null)} />
      )}
    </>
  );
};

export default People;
